const en = {
  nav: {
    home: "Home",
    intelligenceLayers: "Intelligence Layers",
    dataFlow: "Data Flow",
    portalLogin: "Portal Login",
    language: "AR",
    // Legacy keys retained for backward compatibility (some footer/mock usages)
    coreServices: "Core Services",
    extendedServices: "Extended Services",
    dataStreams: "Data Streams",
    apiIntegration: "API & Integration",
    about: "About & Contact",
  },
  hero: {
    badge: "Operated by National Police HQ | Border Control System",
    title: "Al-Ameen",
    subtitle: "Activity Monitoring for Events & Entities Nationally",
    arabicTitle: "الأمين — نظام رصد الأنشطة والأحداث والكيانات الوطنية",
    tagline: "The Nation's Trusted Guardian",
    arabicTagline: "الحارس الأمين للوطن",
    description: "A centralized data collection platform receiving real-time event data from external entities across the nation. Powering national security intelligence through 14 integrated data streams under the Border Control System.",
    exploreServices: "Explore Services",
    apiDocs: "API Documentation",
    hospitalityApp: "Al-Ameen Hospitality App",
    hospitalityNote: "Free PMS for small hotels",
  },
  counters: {
    events: "Events Processed",
    alerts: "Active Alerts",
    sources: "Data Streams",
    entities: "Registered Entities",
  },
  services: {
    title: "Data Collection Streams",
    subtitle: "14 integrated intelligence streams feeding the national security framework",
    coreBadge: "Core",
    extendedBadge: "Extended",
    showEvents: "Show Events",
    hideEvents: "Hide Events",
    core: {
      hotel: {
        category: "Core Intelligence",
        title: "Hotel Intelligence",
        description: "Real-time monitoring of hotel bookings, check-in/out events, room changes, and guest movement across all registered hospitality establishments.",
        events: ["Booking Created", "Booking Modified", "Booking Cancelled", "Guest Check-in", "Guest Check-out", "Room Change", "Early Departure", "Extended Stay"],
      },
      carRental: {
        category: "Core Intelligence",
        title: "Car Rental Monitoring",
        description: "Comprehensive tracking of vehicle rental lifecycle including bookings, pickups, extensions, and drop-offs from all licensed rental operators.",
        events: ["Rental Booking", "Vehicle Pickup", "Rental Extension", "Vehicle Drop-off", "Contract Modification", "Damage Report"],
      },
      mobile: {
        category: "Core Intelligence",
        title: "Mobile Operators",
        description: "SIM card lifecycle management, IMEI tracking, eSIM activations, and international roaming event monitoring from all licensed telecom operators.",
        events: ["SIM Activation", "SIM Deactivation", "IMEI Registration", "eSIM Provisioning", "Roaming Activation", "Number Porting", "SIM Swap"],
      },
      municipality: {
        category: "Core Intelligence",
        title: "Municipality Registry",
        description: "Property lease lifecycle events including new lease registrations, renewals, and terminations from municipal authorities across all governorates.",
        events: ["Lease Start", "Lease Renewal", "Lease Termination", "Property Transfer", "Tenant Change", "Address Update"],
      },
    },
    extended: {
      payment: {
        category: "Financial Intelligence",
        title: "Payment Intelligence",
        description: "Currency exchange transactions, account openings, wire transfers, and large cash transaction monitoring from financial institutions.",
        events: ["Currency Exchange", "Account Opening", "Wire Transfer", "Large Cash Deposit", "Large Cash Withdrawal", "Suspicious Transaction Flag"],
      },
      borders: {
        category: "Border Control",
        title: "Borders & Immigration",
        description: "Entry and exit event feeds from Border Control and eVisa systems, visa compliance monitoring, and overstay detection.",
        events: ["Entry Recorded", "Exit Recorded", "Visa Issued", "Visa Expired", "Overstay Alert", "eVisa Activation", "Border Crossing"],
      },
      health: {
        category: "Health Monitoring",
        title: "Health Interactions",
        description: "Patient registration events, emergency admissions, prescription dispensing, and healthcare facility interactions.",
        events: ["Patient Registration", "Emergency Admission", "Prescription Issued", "Discharge", "Referral Created", "Lab Test Ordered"],
      },
      utility: {
        category: "Infrastructure",
        title: "Utility Activation",
        description: "Electricity, water, and internet service setup, modifications, and terminations linked to residential and commercial addresses.",
        events: ["Electricity Connected", "Water Connected", "Internet Activated", "Service Modified", "Service Terminated", "Address Linked"],
      },
      transport: {
        category: "Mobility Intelligence",
        title: "Public Transport",
        description: "Bus and taxi usage patterns, route monitoring, and public transport interaction data from national transport operators.",
        events: ["Bus Journey", "Taxi Booking", "Route Pattern Flagged", "Frequent Route", "Cross-Governorate Travel"],
      },
      education: {
        category: "Education Registry",
        title: "Educational Enrollment",
        description: "Student enrollment events, academic visa extensions, and educational institution registration from all licensed institutions.",
        events: ["Student Enrolled", "Visa Extension", "Institution Transfer", "Graduation Recorded", "Dropout Flagged"],
      },
      employment: {
        category: "Labor Intelligence",
        title: "Employment Registry",
        description: "Work permit issuances, employer changes, labor contract events, and employment status updates from the Ministry of Labor.",
        events: ["Work Permit Issued", "Employer Change", "Contract Signed", "Contract Terminated", "Permit Renewal", "Permit Cancellation"],
      },
      ecommerce: {
        category: "Commerce Intelligence",
        title: "E-Commerce & Retail",
        description: "Online transaction monitoring, bulk purchase detection, and e-commerce platform activity from registered digital merchants.",
        events: ["Online Purchase", "Bulk Order Flagged", "High-Value Transaction", "Merchant Registration", "Refund Processed"],
      },
      social: {
        category: "Digital Intelligence",
        title: "Social Media Intelligence",
        description: "Phone number to social media account linkage, public keyword monitoring, and digital footprint analysis for national security.",
        events: ["Phone-Social Link", "Keyword Alert", "Account Activity", "Public Post Flagged", "Network Analysis"],
      },
      customs: {
        category: "Customs & Trade",
        title: "Customs & Cargo",
        description: "Import/export declarations, cargo manifests, restricted goods monitoring, and trade-based money laundering detection from customs authorities.",
        events: ["Import Declaration", "Export Declaration", "Cargo Manifest Filed", "Restricted Goods Alert", "TBML Pattern Detected", "Customs Clearance", "Cargo Inspection"],
      },
      marine: {
        category: "Maritime Intelligence",
        title: "Marine & Maritime",
        description: "Marina docking registrations, boat rental tracking, diving activity monitoring, and vessel movement intelligence from coastal and maritime authorities.",
        events: ["Marina Docking", "Boat Rental", "Diving Registration", "Vessel Departure", "Vessel Arrival", "Fishing Permit", "Maritime Incident"],
      },
      postal: {
        category: "Postal Intelligence",
        title: "Postal Services",
        description: "PO Box registrations, parcel receipt tracking, international mail monitoring, and suspicious package detection from national postal authorities.",
        events: ["PO Box Registration", "Package Receipt", "International Parcel", "Suspicious Package Alert", "Bulk Mail Flag", "Address Verification"],
      },
    },
  },
  architecture: {
    title: "Data Flow Architecture",
    subtitle: "Secure multi-layer data pipeline from external entities to risk assessment",
    steps: {
      entities: "External Entities",
      api: "B2B API / Al-Ameen Portal",
      validation: "National Police Validation",
      database: "Central Database",
      vis1: "Security Dept 1",
      vis2: "Security Dept 2",
      risk: "Risk Assessment",
      stored: "Result Stored",
    },
    warning: "Risk results are NEVER shared with external entities",
    note: "Dual screening replication ensures independent risk assessment with zero data leakage",
  },
  hospitality: {
    badge: "Free Desktop App",
    title: "Al-Ameen Hospitality",
    arabicTitle: "الأمين للضيافة",
    description: "A standalone hotel management system designed for small hotels without their own Property Management System. Syncs data to Al-Ameen in the background — completely separate product with its own login.",
    features: ["Booking management & reservations", "Room allocation & housekeeping", "Guest check-in / check-out", "Automatic Al-Ameen data sync", "Offline-capable desktop app", "Zero cost — free for all small hotels"],
    download: "Download for Windows",
    note: "Separate login — not connected to Al-Ameen Portal",
    separator: "Standalone Product",
  },
  api: {
    title: "API & Integration",
    subtitle: "Connect your systems to Al-Ameen via our secure B2B API or the Al-Ameen Portal",
    b2bTitle: "B2B API Integration",
    b2bDesc: "Direct machine-to-machine integration for entities with existing systems. RESTful API with JWT authentication and real-time event streaming.",
    portalTitle: "Al-Ameen Portal",
    portalDesc: "Web-based portal for manual data entry and management. Ideal for smaller entities or as a fallback when API integration is unavailable.",
    features: {
      auth: "JWT Authentication",
      authDesc: "Secure token-based authentication with automatic rotation",
      tls: "TLS 1.3 Encryption",
      tlsDesc: "All data in transit encrypted with industry-standard TLS 1.3",
      rate: "Rate Limiting",
      rateDesc: "Configurable rate limits per entity with burst allowance",
      webhook: "Webhook Support",
      webhookDesc: "Real-time event acknowledgment via configurable webhooks",
      sandbox: "Sandbox Environment",
      sandboxDesc: "Full testing environment mirroring production API",
      support: "24/7 Technical Support",
      supportDesc: "Dedicated National Police technical team for integration assistance",
    },
    endpoints: "Key Endpoints",
    getStarted: "Get Started",
    requestAccess: "Request API Access",
    viewDocs: "View Documentation",
  },
  about: {
    title: "About Al-Ameen",
    subtitle: "Safeguarding the nation through intelligent data collection",
    ropTitle: "National Police Headquarters",
    ropDesc: "Al-Ameen is an initiative of the National Police Headquarters, the primary law enforcement agency of the nation. Operating under the Border Control System national security framework, Al-Ameen represents the nation's commitment to leveraging technology for national safety.",
    ibordersTitle: "Border Control System",
    ibordersDesc: "The Border Control System is the National Police's comprehensive digital border and national security platform. Al-Ameen serves as the data intelligence backbone, collecting and processing events from 14 national data streams to support evidence-based security decisions.",
    missionTitle: "Our Mission",
    missionDesc: "To provide the National Police Headquarters with a unified, real-time intelligence data platform that enables proactive national security management while maintaining the highest standards of data integrity and privacy.",
    contactTitle: "Contact & Support",
    contactDesc: "For API access requests, technical support, or general inquiries about Al-Ameen integration.",
    form: {
      name: "Full Name",
      namePlaceholder: "Your full name",
      email: "Email Address",
      emailPlaceholder: "your@organization.gov",
      organization: "Organization",
      orgPlaceholder: "Your organization name",
      subject: "Subject",
      subjectPlaceholder: "Select a subject",
      subjects: ["API Access Request", "Technical Support", "Integration Inquiry", "General Inquiry", "Al-Ameen Hospitality"],
      message: "Message",
      messagePlaceholder: "Describe your inquiry in detail...",
      submit: "Send Message",
      sending: "Sending...",
      success: "Message sent successfully. Our team will respond within 24 hours.",
      error: "Failed to send message. Please try again.",
    },
  },
  footer: {
    tagline: "The Nation's Trusted Guardian",
    arabicTagline: "الحارس الأمين للوطن",
    description: "Centralized national intelligence data collection platform operated by National Police Headquarters.",
    quickLinks: "Quick Links",
    links: {
      apiDocs: "API Documentation",
      integrationGuide: "Integration Guide",
      supportPortal: "Support Portal",
      statusPage: "System Status",
      hospitality: "Al-Ameen Hospitality",
    },
    contact: "Contact National Police HQ",
    phone: "+968 2456 0000",
    email: "support@alameen.gov",
    address: "National Police Headquarters, Border Control System Division",
    copyright: "© 2025 National Police Headquarters. All rights reserved.",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
    operated: "Operated under Border Control System",
  },
};

export default en;
